import React, { useState, useCallback } from 'react';
import QRCode from 'react-qr-code';
import { ThemeProvider } from 'styled-components';
import { darkTheme } from '@stylesheets/theme';
import useAuth from '@contexts/useAuth';
import { startMfaSetup, verifyMfaSetup } from '@api/auth';
import LoginLayout from '@components/1-atoms/Layouts/LoginLayout/LoginLayout';
import Field from '@components/1-atoms/Field';
import Button, { BUTTON_VARIANT_TEXT } from '@components/1-atoms/Button';
import PageHeader, {
  PAGE_HEADER_VARIANT_LARGE,
} from '@components/2-molecules/PageHeader';
import { ErrorBox } from '../auth.styled';
import {
  Header,
  Logo,
  BrandName,
  MfaForm,
  FooterLinks,
} from '../Login/Login.styled';
import {
  MfaContent,
  MfaFormWrapper,
  Section,
  StepLabel,
  StepDescription,
  QrContainer,
  SecretCode,
  CopyButton,
} from './MfaSetupWall.styled';

type SetupStep = 'start' | 'loading' | 'qr' | 'verifying';

const MfaSetupWall: React.FC = () => {
  const { user, updateMfaEnabled, logout } = useAuth();

  const [step, setStep] = useState<SetupStep>('start');
  const [setupData, setSetupData] = useState<{
    secretCode: string;
    qrUri: string;
  } | null>(null);
  const [code, setCode] = useState('');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleBeginSetup = useCallback(async () => {
    const token = user?.token;
    if (!token) return;
    setError(null);
    setStep('loading');

    try {
      const result = await startMfaSetup(token);
      setSetupData({ secretCode: result.secretCode, qrUri: result.qrUri });
      setStep('qr');
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : 'Failed to start MFA setup';
      setError(msg);
      setStep('start');
    }
  }, [user]);

  const handleVerify = useCallback(async () => {
    const token = user?.token;
    if (!token || !code.trim()) return;
    setError(null);
    setStep('verifying');

    try {
      await verifyMfaSetup(token, code.trim());
      updateMfaEnabled(true);
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : 'Failed to verify MFA code';
      setError(msg);
      setStep('qr');
      setCode('');
    }
  }, [user, code, updateMfaEnabled]);

  const handleVerifySubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (code.trim().length === 6) {
        handleVerify();
      }
    },
    [code, handleVerify],
  );

  const handleCodeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setCode(e.target.value.replace(/\D/g, '').slice(0, 6));
    },
    [],
  );

  const handleCopySecret = useCallback(async (secret: string) => {
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API unavailable
    }
  }, []);

  return (
    <ThemeProvider theme={darkTheme}>
      <LoginLayout>
        <Header>
          <Logo />
          <BrandName>Aibuild OS</BrandName>
        </Header>

        <MfaFormWrapper as="div">
          <MfaContent>
            <PageHeader
              variant={PAGE_HEADER_VARIANT_LARGE}
              title="Two-Factor Authentication Required"
              subtitle="Your organization requires 2FA for all members. Set it up to continue."
            />

            {error && <ErrorBox>{error}</ErrorBox>}

            {step === 'start' && (
              <Section>
                <StepDescription>
                  You&apos;ll need an authenticator app such as Google
                  Authenticator, Authy, or 1Password to generate verification
                  codes.
                </StepDescription>
                <Button onClick={handleBeginSetup}>Set Up 2FA</Button>
              </Section>
            )}

            {step === 'loading' && (
              <Section>
                <StepDescription>
                  Generating your authenticator secret&hellip;
                </StepDescription>
              </Section>
            )}

            {(step === 'qr' || step === 'verifying') && setupData && (
              <Section>
                <StepLabel>Step 1: Scan QR Code</StepLabel>
                <StepDescription>
                  Open your authenticator app and scan the QR code below. If you
                  can&apos;t scan, enter the secret key manually.
                </StepDescription>

                <QrContainer>
                  <QRCode value={setupData.qrUri} size={180} level="M" />
                </QrContainer>

                <SecretCode>
                  <span>{setupData.secretCode}</span>
                  <CopyButton
                    onClick={() => handleCopySecret(setupData.secretCode)}
                  >
                    {copied ? 'Copied!' : 'Copy'}
                  </CopyButton>
                </SecretCode>

                <StepLabel>Step 2: Enter Verification Code</StepLabel>
                <StepDescription>
                  Enter the 6-digit code shown in your authenticator app.
                </StepDescription>

                <MfaForm onSubmit={handleVerifySubmit}>
                  <Field
                    type="text"
                    placeholder="6-digit code"
                    value={code}
                    onChange={handleCodeChange}
                    maxLength={6}
                    disabled={step === 'verifying'}
                    autoFocus
                  />
                  <Button
                    type="submit"
                    disabled={
                      code.trim().length !== 6 || step === 'verifying'
                    }
                    loading={step === 'verifying'}
                  >
                    {step === 'verifying' ? 'Verifying\u2026' : 'Verify & Enable'}
                  </Button>
                </MfaForm>
              </Section>
            )}
          </MfaContent>
        </MfaFormWrapper>

        <FooterLinks>
          <Button variant={BUTTON_VARIANT_TEXT} onClick={logout}>
            Log out
          </Button>
        </FooterLinks>
      </LoginLayout>
    </ThemeProvider>
  );
};

export default MfaSetupWall;
